import PropTypes from 'prop-types';
import React from 'react';
import ActionSpinner from 'src/components/loaders/action-spinner';
import ErrorDisplay from 'src/components/ui/ErrorDisplay';

/**
 * Wrapper component for action panel content that handles loading and error states.
 * This provides a consistent pattern for all action panels without needing to
 * import ActionSpinner in every component.
 *
 * @example
 * const MyPanel = ({ data, isLoading, error, onClose }) => (
 *   <ActionPanelWrapper isLoading={isLoading} error={error} onClose={onClose}>
 *     <div>Your content with {data}</div>
 *   </ActionPanelWrapper>
 * );
 */
const ActionPanelWrapper = ({ isLoading, error, children, errorMessage, onClose }) => {
  if (isLoading) {
    return <ActionSpinner />;
  }

  if (error) {
    return (
      <ErrorDisplay
        title='Unable to Load Data'
        message={
          errorMessage ||
          error?.message ||
          'Something went wrong while loading the data. Please try again.'
        }
        buttonText='Go Back'
        onButtonClick={onClose}
      />
    );
  }

  return children;
};

ActionPanelWrapper.propTypes = {
  isLoading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  errorMessage: PropTypes.string,
  onClose: PropTypes.func,
  children: PropTypes.node.isRequired,
};

ActionPanelWrapper.defaultProps = {
  isLoading: false,
  error: null,
  errorMessage: null,
  onClose: null,
};

export default ActionPanelWrapper;
